import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@fluentry/ui";
import { Link } from "@tanstack/react-router";
import { AlertCircle, Building2, LoaderCircle, RotateCw, Users2 } from "lucide-react";
import React from "react";
import type { AuthSessionData } from "../context/app-context";
import { productAppAuthRequest } from "../lib/auth-client";
import { captureProductAppException } from "../lib/sentry";

type OrganizationRecord = {
  id: string;
  name: string;
  organizationType?: string;
};

type InvitationRecord = {
  id: string;
};

const PERSONAL_WORKSPACE_VALUE = "__personal_workspace__";

export type WorkspaceAuthRequest = typeof productAppAuthRequest;

function formatWorkspaceError(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function resolveWorkspaceLabel(
  activeOrganization: OrganizationRecord | null,
  activeOrganizationId: string | null,
  organizationListLoadError: string | null,
) {
  // Preserve the active-organization error signal instead of implying the user is in a personal workspace.
  if (organizationListLoadError && activeOrganizationId && !activeOrganization) {
    return "Active workspace unavailable";
  }

  return activeOrganization?.name || "Personal workspace";
}

export function WorkspaceSwitcher({
  authSession,
  onRefetchSession,
  onNavigate,
  authRequest = productAppAuthRequest,
}: {
  authSession: AuthSessionData | null;
  onRefetchSession: () => Promise<void>;
  onNavigate?: () => void;
  authRequest?: WorkspaceAuthRequest;
}) {
  const [organizations, setOrganizations] = React.useState<OrganizationRecord[]>([]);
  const [pendingInvitations, setPendingInvitations] = React.useState<InvitationRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [organizationListLoadError, setOrganizationListLoadError] = React.useState<string | null>(
    null,
  );
  const [pendingInvitationsLoadError, setPendingInvitationsLoadError] = React.useState<
    string | null
  >(null);
  const [switchingValue, setSwitchingValue] = React.useState<string | null>(null);
  const activeOrganizationId =
    typeof authSession?.session.activeOrganizationId === "string"
      ? authSession.session.activeOrganizationId
      : null;

  const loadOrganizations = React.useCallback(async () => {
    if (!authSession?.user) {
      setOrganizations([]);
      setPendingInvitations([]);
      setError(null);
      setOrganizationListLoadError(null);
      setPendingInvitationsLoadError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setOrganizationListLoadError(null);
    setPendingInvitationsLoadError(null);

    // The shell needs its own lightweight workspace state so people can switch context without leaving the editor.
    const [nextOrganizationsResult, nextPendingInvitationsResult] = await Promise.allSettled([
      authRequest<OrganizationRecord[]>("/organization/list", { method: "GET" }),
      authRequest<InvitationRecord[]>("/organization/list-user-invitations", {
        method: "GET",
      }),
    ]);

    if (nextOrganizationsResult.status === "fulfilled") {
      setOrganizations(nextOrganizationsResult.value);
    } else {
      void captureProductAppException(nextOrganizationsResult.reason, {
        action: "load-workspaces",
        feature: "workspace-switcher",
      });
      setOrganizations([]);
      setOrganizationListLoadError(
        formatWorkspaceError(nextOrganizationsResult.reason, "Failed to load workspaces"),
      );
    }

    if (nextPendingInvitationsResult.status === "fulfilled") {
      setPendingInvitations(nextPendingInvitationsResult.value);
    } else {
      void captureProductAppException(nextPendingInvitationsResult.reason, {
        action: "load-workspace-invitations",
        feature: "workspace-switcher",
      });
      setPendingInvitations([]);
      setPendingInvitationsLoadError(
        formatWorkspaceError(
          nextPendingInvitationsResult.reason,
          "Failed to load workspace invitations",
        ),
      );
    }

    setLoading(false);
  }, [authRequest, authSession?.user]);

  React.useEffect(() => {
    void loadOrganizations();
  }, [loadOrganizations, activeOrganizationId]);

  const activeOrganization =
    organizations.find((entry) => entry.id === activeOrganizationId) ?? null;
  const selectedValue = activeOrganizationId ?? PERSONAL_WORKSPACE_VALUE;
  const workspaceLoadUnavailable =
    organizationListLoadError !== null && switchingValue === null && !loading;

  async function handleWorkspaceChange(nextValue: string) {
    if (nextValue === selectedValue) {
      return;
    }

    setSwitchingValue(nextValue);
    setError(null);

    try {
      await authRequest("/organization/set-active", {
        method: "POST",
        body: {
          organizationId: nextValue === PERSONAL_WORKSPACE_VALUE ? null : nextValue,
        },
      });
      await onRefetchSession();
      await loadOrganizations();
      onNavigate?.();
    } catch (switchError) {
      void captureProductAppException(switchError, {
        action: "switch-workspace",
        feature: "workspace-switcher",
        tags: {
          next_workspace: nextValue,
        },
      });
      setError(switchError instanceof Error ? switchError.message : "Failed to switch workspace");
    } finally {
      setSwitchingValue(null);
    }
  }

  if (!authSession?.user) {
    return null;
  }

  return (
    <div className="mb-6 grid gap-3 rounded-[1.25rem] border border-sidebar-border bg-card px-3 py-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-0.5">
          <span className="text-[10px] font-semibold tracking-[0.22em] text-sidebar-foreground/60 uppercase">
            Workspace
          </span>
          <strong className="text-sm font-semibold text-foreground">
            {resolveWorkspaceLabel(
              activeOrganization,
              activeOrganizationId,
              organizationListLoadError,
            )}
          </strong>
        </div>
        {pendingInvitations.length > 0 ? (
          <Badge
            variant="outline"
            className="border-border bg-muted/30 text-[10px] text-muted-foreground shadow-none"
          >
            {pendingInvitations.length} invite{pendingInvitations.length === 1 ? "" : "s"}
          </Badge>
        ) : null}
      </div>

      <Select value={selectedValue} onValueChange={(value) => void handleWorkspaceChange(value)}>
        <SelectTrigger
          className="h-11 rounded-[0.95rem] border-sidebar-border bg-background text-sm text-foreground shadow-none"
          disabled={loading || switchingValue !== null || workspaceLoadUnavailable}
          aria-label="Switch active workspace"
        >
          <SelectValue placeholder={loading ? "Loading workspaces..." : "Select workspace"} />
        </SelectTrigger>
        <SelectContent position="popper" className="rounded-[0.95rem] border-border bg-background">
          <SelectGroup>
            <SelectItem value={PERSONAL_WORKSPACE_VALUE}>Personal workspace</SelectItem>
            {organizations.map((entry) => (
              <SelectItem key={entry.id} value={entry.id}>
                {entry.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      <div className="grid gap-2">
        <div className="flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground">
          {switchingValue !== null || loading ? (
            <>
              <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
              <span>
                {switchingValue !== null ? "Switching workspace..." : "Loading workspaces..."}
              </span>
            </>
          ) : workspaceLoadUnavailable ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-destructive" />
              <span className="min-w-0 text-destructive">Workspace list unavailable</span>
            </>
          ) : pendingInvitationsLoadError ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-destructive" />
              <span className="min-w-0 text-destructive">Workspace invites unavailable</span>
            </>
          ) : (
            <>
              <Building2 className="h-3.5 w-3.5" />
              <span>
                {organizations.length > 0
                  ? `${organizations.length} shared workspace${organizations.length === 1 ? "" : "s"}`
                  : "No shared workspaces yet"}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-1.5">
          {workspaceLoadUnavailable ? (
            <Button
              type="button"
              variant="ghost"
              className="h-8 rounded-full px-3 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => void loadOrganizations()}
            >
              <RotateCw className="mr-1.5 h-3.5 w-3.5" />
              Retry
            </Button>
          ) : null}
          <Button
            asChild
            variant="ghost"
            className="h-8 rounded-full px-3 text-xs text-sidebar-primary hover:bg-sidebar-primary/10 hover:text-sidebar-primary"
          >
            <Link to="/settings/organizations" onClick={onNavigate}>
              <Users2 className="mr-1.5 h-3.5 w-3.5" />
              Manage
            </Link>
          </Button>
        </div>
      </div>

      {organizationListLoadError ? (
        <p className="text-[11px] text-destructive">{organizationListLoadError}</p>
      ) : null}
      {pendingInvitationsLoadError ? (
        <p className="text-[11px] text-destructive">{pendingInvitationsLoadError}</p>
      ) : null}
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}
